// src/components/lesson/diagrams/PolygonShape.tsx
//
// Arbitrary 2D polygon from explicit vertices — covers the shapes the regular
// `polygon` and `triangle` types can't: trapezoids, parallelograms, kites,
// irregular quadrilaterals, and composite 2D figures (area questions). Vertices
// are given in math coordinates and auto-fit + centered into the canvas (y up).
//
// Params:
//   vertices: [{ x, y, label? }]   // >= 3 points, in order around the polygon
//   sideLabels?: string[]          // label for edge i (vertex i → i+1)
//   rightAngles?: number[]         // vertex indices to mark with a right-angle square
//   label?: string

type Vertex = { x: number; y: number; label?: string }
type ShapeParams = {
  vertices?: Vertex[]
  sideLabels?: string[]
  rightAngles?: number[]
  label?: string
}

const W = 340
const H = 260
const PAD = 40
const SHAPE = 'rgb(148 163 184)'
const FILL = 'rgb(34 211 238 / 0.10)'
const SIDE = 'rgb(34 211 238)'
const VERTEX = 'rgb(148 163 184)'

type Pt = { x: number; y: number }

/** Fit math-space points (y up) into the canvas, centered, preserving aspect. */
function fit(verts: Pt[]): Pt[] {
  const xs = verts.map((v) => v.x)
  const ys = verts.map((v) => v.y)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const w = Math.max(maxX - minX, 1e-6)
  const h = Math.max(maxY - minY, 1e-6)
  const scale = Math.min((W - 2 * PAD) / w, (H - 2 * PAD) / h)
  const offX = (W - w * scale) / 2
  const offY = (H - h * scale) / 2
  return verts.map((v) => ({ x: offX + (v.x - minX) * scale, y: offY + (maxY - v.y) * scale }))
}

export default function PolygonShape({ params }: { params: Record<string, unknown> }) {
  const p = params as ShapeParams
  const raw = (Array.isArray(p.vertices) ? p.vertices : []).filter(
    (v): v is Vertex => typeof v?.x === 'number' && typeof v?.y === 'number',
  )
  // Default to a generic trapezoid if no valid vertices were given.
  const mathVerts: Vertex[] =
    raw.length >= 3
      ? raw
      : [
          { x: 0, y: 0 },
          { x: 6, y: 0 },
          { x: 5, y: 4 },
          { x: 1, y: 4 },
        ]
  const fitted = fit(mathVerts)
  const cx = fitted.reduce((s, v) => s + v.x, 0) / fitted.length
  const cy = fitted.reduce((s, v) => s + v.y, 0) / fitted.length
  const sideLabels = Array.isArray(p.sideLabels) ? p.sideLabels : []
  const rightAngles = Array.isArray(p.rightAngles) ? p.rightAngles : []

  // Push a label outward from the centroid by `amt` px.
  const outward = (pt: Pt, amt: number): Pt => {
    const d = Math.max(Math.hypot(pt.x - cx, pt.y - cy), 1e-6)
    return { x: pt.x + ((pt.x - cx) * amt) / d, y: pt.y + ((pt.y - cy) * amt) / d }
  }

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={p.label ?? 'Polygon'}
      className="my-3 w-full max-w-md rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      <polygon
        points={fitted.map((v) => `${v.x},${v.y}`).join(' ')}
        fill={FILL}
        stroke={SHAPE}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />

      {/* Right-angle marks at the requested vertices */}
      {rightAngles
        .filter((i) => i >= 0 && i < fitted.length)
        .map((i) => {
          const v = fitted[i]
          const prev = fitted[(i - 1 + fitted.length) % fitted.length]
          const next = fitted[(i + 1) % fitted.length]
          const u1 = unit(v, prev)
          const u2 = unit(v, next)
          const s = 10
          const a = { x: v.x + u1.x * s, y: v.y + u1.y * s }
          const b = { x: v.x + u2.x * s, y: v.y + u2.y * s }
          const c = { x: a.x + u2.x * s, y: a.y + u2.y * s }
          return (
            <polyline
              key={`ra-${i}`}
              points={`${a.x},${a.y} ${c.x},${c.y} ${b.x},${b.y}`}
              fill="none"
              stroke={SHAPE}
              strokeWidth="1.25"
            />
          )
        })}

      {/* Side labels at each edge midpoint, nudged outward */}
      {fitted.map((v, i) => {
        const text = sideLabels[i]
        if (!text) return null
        const n = fitted[(i + 1) % fitted.length]
        const mid = { x: (v.x + n.x) / 2, y: (v.y + n.y) / 2 }
        const lp = outward(mid, 14)
        return (
          <text
            key={`s-${i}`}
            x={lp.x}
            y={lp.y}
            fontSize="12"
            fontWeight="600"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={SIDE}
          >
            {text}
          </text>
        )
      })}

      {/* Vertex labels, nudged outward */}
      {fitted.map((v, i) => {
        const text = mathVerts[i]?.label
        if (!text) return null
        const lp = outward(v, 14)
        return (
          <text
            key={`v-${i}`}
            x={lp.x}
            y={lp.y}
            fontSize="12"
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={VERTEX}
          >
            {text}
          </text>
        )
      })}
    </svg>
  )
}

function unit(from: Pt, to: Pt): Pt {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const d = Math.max(Math.hypot(dx, dy), 1e-6)
  return { x: dx / d, y: dy / d }
}
